import { logMessage } from './sentry';
import { templateMacros } from './url-utils';
import { replacerWithoutPattern } from './utils';
import { DebugLog } from './debug-log';

interface ScriptAttribute {
  name: string;
  value: string;
}

interface Options {
  overloadDocumentWrite: boolean;
  enableSentryLog: boolean;
  allowInjectIntoBody: boolean;
}

/**
 * @param code is a string containing all code to inject
 * @param htmlContainerSelector string is the CSS selector where HTML will be injected
 * @param scriptsContainerSelector string is the CSS selector where script will be injected
 * @param options list of Options
 */

export function injectTags(
  code: string,
  htmlContainerSelector: string,
  scriptsContainerSelector: string,
  fallbackContainerSelector: string,
  options: Options
): void {
  if (options.overloadDocumentWrite) {
    overloadDocumentWrite(
      htmlContainerSelector,
      scriptsContainerSelector,
      fallbackContainerSelector,
      options
    );
  }

  const codeToInject = new CodeManager(code)
    .replaceMacros()
    .cleanSpacesBetweenTags()
    .cleanHTML()
    // .cleanCeltraContainerScript()
    // .cleanExtraMraidTag()
    .removeTags('noscript');

  injectNonScriptTags(
    codeToInject.extractCodeToInject(),
    htmlContainerSelector,
    fallbackContainerSelector,
    options.allowInjectIntoBody
  );
  injectScriptTags(
    codeToInject.extractScriptTags(),
    scriptsContainerSelector,
    fallbackContainerSelector
  );
}

function overloadDocumentWrite(
  nonScriptContainerRef: string,
  scriptsContainerRef: string,
  fallbackContainerSelector: string,
  options: Options
): void {
  (window.document as any).writeln = window.document.write = function(
    ...args: any[]
  ): void {
    const argsString: string = Array.prototype.slice
      .call(arguments)
      .join('')
      .trim();
    // eslint-disable-next-line
    DebugLog.log('document.write called with: ', argsString);
    if (options.enableSentryLog) {
      if (argsString.charAt(0) !== '<') {
        logMessage(
          'An injected code (display tracker or mraid tag) uses "document.write" method for this code',
          {
            code: argsString
          }
        );
      }
    }

    injectTags(
      argsString,
      nonScriptContainerRef,
      scriptsContainerRef,
      fallbackContainerSelector,
      {
        ...options,
        overloadDocumentWrite: false
      }
    );
  };
}

function injectNonScriptTags(
  code: string,
  containerSelector: string,
  defaultContainerSelector: string,
  allowedInBody: boolean = false
): void {
  const container =
    document.querySelector(containerSelector) ||
    document.querySelector(defaultContainerSelector);
  if (!allowedInBody && container === document.body) {
    console.error(
      'Try to wipe body with display trackers. display trackers injection stops'
    );
  } else if (code) {
    // WARNING: a script display trackers may found this images instead of the real creative
    // for debug try to set dt_force_html_container to '#root'
    const subContainer = document.createElement('div');
    subContainer.style.width = '100%';
    subContainer.style.height = '100%';
    subContainer.innerHTML = code;
    container && container.appendChild(subContainer);
  }
}

function injectScriptTags(
  scriptTags: Array<ScriptTag>,
  containerSelector: string,
  defaultContainerSelector: string
): void {
  const tryParent =
    defaultContainerSelector === containerSelector &&
    containerSelector !== 'body';

  let container = document.querySelector(containerSelector);
  if (container && tryParent) {
    container = container.parentElement;
  }
  if (!container) {
    container = document.querySelector(defaultContainerSelector);
  }

  scriptTags.forEach((scriptTag: ScriptTag) => {
    if (scriptTag.delay) {
      setTimeout(() => {
        if (container) {
          container.appendChild(scriptTag.toDomElement());
        }
      }, scriptTag.delay);
    } else if (container) {
      container.appendChild(scriptTag.toDomElement());
    }
  });
}

/**
 * @class CodeManager
 * Manage the code string to inject
 */
export class CodeManager {
  code: string;

  constructor(code: string) {
    this.code = code;
  }

  cleanExtraMraidTag(): CodeManager {
    const langJs = '( language="javascript"){0,1}';
    const typeJs = '( type="text/javascript"){0,1}';
    return new CodeManager(
      replacerWithoutPattern(
        this.cleanSpacesBetweenTags().code,
        new RegExp(
          `<script${langJs}${typeJs}${langJs} src="mraid.js"${langJs}${typeJs}${langJs}></script>`,
          'gi'
        ),
        ''
      )
    );
  }

  cleanCeltraContainerScript(): CodeManager {
    let script = replacerWithoutPattern(
      this.cleanSpacesBetweenTags().code,
      '<script type="text/adtag">',
      ''
    );
    return new CodeManager(
      script.replace(new RegExp('</([a-zA-Z]+)></script>', 'i'), '</$1>')
    );
  }

  cleanHTML(): CodeManager {
    return new CodeManager(
      replacerWithoutPattern(this.code, /><\/IMG>/gi, '/>')
    );
  }

  cleanSpacesBetweenTags(): CodeManager {
    return new CodeManager(
      replacerWithoutPattern(this.code, />[\s]+</gm, '><').trim()
    );
  }

  replaceMacros(): CodeManager {
    const macros = (window as any).mraid
      ? (window as any).mraid.getMaxSize()
      : {};
    return new CodeManager(templateMacros(this.code, macros));
  }

  removeTags(tagName: string): CodeManager {
    const re = this.getTagSelector(tagName);
    return new CodeManager(replacerWithoutPattern(this.code, re, ''));
  }

  extractCodeToInject(): string {
    return this.removeTags('script').code.trim();
  }

  extractScriptTags(): Array<ScriptTag> {
    const re = this.getTagSelector('script');
    const tagsContents: ScriptTag[] = [];
    let match: RegExpExecArray | null;
    while ((match = re.exec(this.code)) !== null) {
      const code = match[2] || '';
      const attributes = match[1] || '';
      tagsContents.push(new ScriptTag(code, attributes));
    }
    return tagsContents;
  }

  getTagSelector(tag: string): RegExp {
    // RegExp : all ${code} tag with actual content and potential attributes non greedy
    // group1 : attributes / group2 : tag innerHTML
    // work with : https://regex101.com/r/Ms1owH/2
    return new RegExp(
      // eslint-disable-next-line
      `<${tag}([^>]*)>((?!<\\\/${tag}>)[\\\s\\\S]*?)?<\\\/${tag}>`,
      'gmi'
    );
  }
}

/**
 * @class ScriptTag
 * Manage a script tag
 */
export class ScriptTag {
  content: string;
  attributes: Array<ScriptAttribute>;
  delay: number;

  /**
   * @param content is a string containing the code inside <script> tag
   * @param stringAttributes is a string with attribute like 'key="value" key2="value2" ...'
   */
  constructor(content: string, stringAttributes: string = '') {
    this.content = content.trim();
    this.delay = 0;
    this.attributes = this.parseAttributes(stringAttributes);
  }

  toDomElement(): HTMLElement {
    const newScriptTag = document.createElement('script');
    newScriptTag.type = 'text/javascript';
    this.attributes.forEach(function(attribute: ScriptAttribute) {
      newScriptTag.setAttribute(attribute.name, attribute.value);
    });
    newScriptTag.textContent = this.content;
    return newScriptTag;
  }

  parseAttributes(stringAttributes: string): Array<ScriptAttribute> {
    const rawAttributes = this.cleanAttributes(
      this.preserveAsync(stringAttributes)
    );
    if (!rawAttributes) {
      return [];
    }
    // RegExp : html attribute parser
    // group1 : attribut name / group2 : attribute value / group3 : attribute value when no quotes
    // work with : https://regex101.com/r/yyxf2Q/1
    const re = new RegExp(
      /([^= ]+)=(?:["']((?:.(?!["']?\s+(?:\S+)=|[>]))+.)["']|(\w+))\s/gim
    );
    const attributes: Array<ScriptAttribute> = [];
    // eslint-disable-next-line
    let urlAttributeAsLast: ScriptAttribute | undefined;
    let match: RegExpExecArray | null;
    // add ending space to fully parse attributs with js in it
    while ((match = re.exec(rawAttributes + ' ')) !== null) {
      const name = match[1];
      const value = match[2] || match[3];
      if (name && value) {
        if (name === 'url') {
          urlAttributeAsLast = { name, value };
        } else if (name === 'delay') {
          this.delay = parseInt(value, 10);
        } else {
          attributes.push({ name, value });
        }
      }
    }
    if (urlAttributeAsLast) {
      attributes.push(urlAttributeAsLast);
    }
    return attributes;
  }

  preserveAsync(attributes: string): string {
    return ['async', 'defer'].reduce((current, attr) => {
      return current.replace(
        new RegExp(` ${attr}([^=])`, 'i'),
        ` ${attr}="${attr}" $1`
      );
    }, attributes);
  }

  cleanAttributes(attributes: string): string {
    // remove multiple whitespace
    attributes = replacerWithoutPattern(attributes, /\s+/gim, ' ');
    // remove spaces after "=" allowed in HTML spec.
    attributes = replacerWithoutPattern(attributes, /=\s+/gim, '=');
    return attributes.trim();
  }
}
